import {
  CalendarDataParameters,
  CalendarDataParams,
  CalendarEvents,
  CalendarParamsMap,
  Conference,
  DateSortField,
  Dividend,
  Earnings,
  Economics,
  FDA,
  Guidance,
  Importance,
  IPOs,
  IPOType,
  MergersAndAcquisitions,
  Ratings,
  RatingsAction,
  SEC,
  UnusualOptionsActivity,
} from '../entities';
import { commaSeparatedSymbols, isEmpty } from '@benzinga/utils';
import { getNumberSuffix, abbreviateNumber } from '@benzinga/utils/numberUtils';
import { DateTime } from 'luxon';
import i18n, { translate } from '@benzinga/translate';

type TranslateFn = typeof translate;

//capitalize the first character of the string
export const capitalize = (name: string): string => name.charAt(0).toUpperCase() + name.slice(1);

export const titleCase = (title: string): string =>
  title
    .split(' ')
    .map(name => capitalize(name))
    .join(' ');

export const formatRequestDate = (date: Date | string): string => {
  const format = 'yyyy-MM-dd';
  if (typeof date === 'string') {
    return DateTime.fromISO(date).toFormat(format);
  }

  return DateTime.fromJSDate(date).toFormat(format);
};

export const getLastestDate = (calendarData: CalendarEvents[]) => {
  const item = calendarData && calendarData.sort((a: CalendarEvents, b: CalendarEvents) => b.updated - a.updated)[0];
  const result = item && new Date(item.updated * 1000).toISOString();
  return result;
};

export const sortByField = (calendarData: CalendarEvents[], field: string) => {
  return calendarData && calendarData.sort((a: CalendarEvents, b: CalendarEvents) => a[field] - b[field]);
};

type CalendarEventsWithDate =
  | FDA
  | Ratings
  | Conference
  | Earnings
  | Dividend
  | Economics
  | Guidance
  | IPOs
  | MergersAndAcquisitions
  | UnusualOptionsActivity
  | SEC;

export const getFutureEvents = (events: CalendarEventsWithDate[]) => {
  return events?.filter(
    (event: CalendarEventsWithDate) => DateTime.fromISO(event.date).startOf('day') >= DateTime.now().startOf('day'),
  );
};

export const getPastEvents = (events: CalendarEventsWithDate[]) => {
  return events?.filter(
    (event: CalendarEventsWithDate) => DateTime.fromISO(event.date).startOf('day') < DateTime.now().startOf('day'),
  );
};

export const recalculateParameters = (
  events: CalendarEvents[],
  parameters: CalendarDataParameters,
): CalendarDataParameters => {
  const updated = Math.max(0, ...events.map(item => item.updated));
  return {
    ...parameters,
    updated,
  };
};

export const isEqualArrShallow = (arr1: unknown[], arr2: unknown[]): boolean =>
  Array.isArray(arr1) &&
  Array.isArray(arr2) &&
  arr1.length === arr2.length &&
  arr1.every((el, index) => el === arr2[index]);

// eslint-disable-next-line @typescript-eslint/ban-types
export const omit = <T extends object>(obj: T, ...args: (keyof T)[]): T =>
  (Object.keys(obj) as (keyof T)[]).reduce((omitted, key) => {
    if (!args.includes(key)) {
      omitted[key] = obj[key];
    }
    return omitted;
  }, {} as T);

export type CalendarInputParams =
  | CalendarParamsMap[keyof CalendarParamsMap]
  | CalendarDataParams
  | Record<string, unknown>;

const getSymbols = (obj: Record<string, unknown>): string | undefined => {
  const symbols = obj.symbols;
  if (Array.isArray(symbols) && symbols.length > 0) {
    return symbols.join(',').toUpperCase();
  }
  if (typeof symbols === 'string' && symbols) {
    return symbols;
  }
  return undefined;
};

const getSecurities = (obj: Record<string, unknown>): string | undefined => commaSeparatedSymbols(obj.securities);

export const setParams = (input: CalendarInputParams): CalendarDataParams => {
  const obj = input as Record<string, unknown>;
  const params: CalendarDataParams = {
    pageSize: (obj.pageSize ?? 1000) as number,
  };

  const action = obj.action as RatingsAction | undefined;
  const analyst = obj.analyst as string | undefined;
  const analystId = obj.analystId as string | undefined;
  const date = obj.date as string | undefined;
  const dateFrom = obj.dateFrom as string | Date | undefined;
  const dateSearchField = obj.dateSearchField as string | undefined;
  const dateSort = obj.dateSort as DateSortField | undefined;
  const dateSortStrict = obj.dateSortStrict as boolean | undefined;
  const dateTo = obj.dateTo as string | Date | undefined;
  const dividendYield = obj.dividendYield as number | undefined;
  const dividendYieldOperation = obj.dividendYieldOperation as string | undefined;
  const fields = obj.fields as string | undefined;
  const firm = obj.firm as string | undefined;
  const firmId = obj.firmId as string | undefined;
  const id = obj.id as string | undefined;
  const importance = obj.importance as Importance | undefined;
  const ipoType = obj.ipoType as IPOType | undefined;
  const page = obj.page as number | undefined;
  const securities = getSecurities(obj);
  const symbols = getSymbols(obj);
  const updated = obj.updated as number | undefined;
  const isPrimary = obj.isPrimary as boolean | undefined;
  const country = obj.country as string | undefined;
  const eventName = obj.eventName as string | undefined;
  const token = obj.token as string | undefined;

  if (action) params.action = action;
  if (analystId || analyst) params.analystId = analystId ?? analyst;
  if (date) params.date = formatRequestDate(date);
  if (dateFrom) params.dateFrom = formatRequestDate(dateFrom);
  if (dateSearchField) params.dateSearchField = dateSearchField;
  if (dateSort) params.dateSort = dateSort;
  if (dateSortStrict !== undefined) params.dateSortStrict = dateSortStrict;
  if (dateTo) params.dateTo = formatRequestDate(dateTo);
  if (dividendYield !== undefined) params.dividendYield = dividendYield;
  if (dividendYieldOperation) params.dividendYieldOperation = dividendYieldOperation;
  if (fields) params.fields = fields;
  if (firm) params.firm = firm;
  if (firmId) params.firmId = firmId;
  if (id) params.id = id;
  if (importance !== undefined) params.importance = importance;
  if (ipoType) params.ipoType = ipoType;
  if (page !== undefined) params.page = page;
  if (securities) params.securities = securities;
  if (symbols) params.symbols = symbols;
  if (updated !== undefined) params.updated = updated;
  if (isPrimary !== undefined) params.isPrimary = isPrimary;
  if (country) params.country = country;
  if (eventName) params.eventName = eventName;
  if (token) params.token = token;

  return params;
};

export type CalendarWireParams = Record<string, string | number | boolean | undefined>;

export const toUpstreamWireParams = (input: CalendarInputParams): CalendarWireParams => {
  const params = setParams(input);
  const wire: CalendarWireParams = {};

  if (params.pageSize != null) wire.pagesize = params.pageSize;
  if (params.page != null) wire.page = params.page;
  if (params.fields) wire.fields = params.fields;
  if (params.token) wire.token = params.token;
  if (params.action) wire['parameters[action]'] = params.action;
  if (params.analystId) wire['parameters[analyst_id]'] = params.analystId;
  if (params.date) wire['parameters[date]'] = params.date;
  if (params.dateFrom) wire['parameters[date_from]'] = params.dateFrom;
  if (params.dateSearchField) wire['parameters[date_search_field]'] = params.dateSearchField;
  if (params.dateSort) wire['parameters[date_sort]'] = params.dateSort;
  if (params.dateSortStrict !== undefined) wire['parameters[date_sort_strict]'] = params.dateSortStrict;
  if (params.dateTo) wire['parameters[date_to]'] = params.dateTo;
  if (params.dividendYield !== undefined) wire['parameters[dividend_yield]'] = params.dividendYield;
  if (params.dividendYieldOperation) wire['parameters[dividend_yield_operation]'] = params.dividendYieldOperation;
  if (params.firm) wire['parameters[firm]'] = params.firm;
  if (params.firmId) wire['parameters[firm_id]'] = params.firmId;
  if (params.id) wire['parameters[id]'] = params.id;
  if (params.importance !== undefined) wire['parameters[importance]'] = params.importance;
  if (params.ipoType) wire['parameters[ipo_type]'] = params.ipoType;
  if (params.securities) wire['parameters[securities]'] = params.securities;
  if (params.symbols) wire['parameters[tickers]'] = params.symbols;
  if (params.updated !== undefined) wire['parameters[updated]'] = params.updated;
  if (params.isPrimary !== undefined) wire['parameters[is_primary]'] = params.isPrimary;
  // Economics docs: country / event_name are top-level query params, not parameters[...]
  if (params.country) wire.country = params.country;
  if (params.eventName) wire.event_name = params.eventName;

  return wire;
};

export const toProxyQueryParams = (input: CalendarInputParams): Record<string, string | number> => {
  const params = setParams(input);
  const query: Record<string, string | number> = {};

  if (params.pageSize != null) query.pageSize = params.pageSize;
  if (params.page != null) query.page = params.page;
  if (params.fields) query.fields = params.fields;
  if (params.action) query.action = params.action;
  if (params.analystId) query.analystId = params.analystId;
  if (params.date) query.date = params.date;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateSearchField) query.dateSearchField = params.dateSearchField;
  if (params.dateSort) query.dateSort = params.dateSort;
  if (params.dateSortStrict !== undefined) query.dateSortStrict = params.dateSortStrict ? 1 : 0;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.dividendYield !== undefined) query.dividendYield = params.dividendYield;
  if (params.dividendYieldOperation) query.dividendYieldOperation = params.dividendYieldOperation;
  if (params.firm) query.firm = params.firm;
  if (params.firmId) query.firmId = params.firmId;
  if (params.id) query.id = params.id;
  if (params.importance !== undefined) query.importance = params.importance;
  if (params.ipoType) query.ipoType = params.ipoType;
  if (params.securities) query.securities = params.securities;
  if (params.symbols) query.symbols = params.symbols;
  if (params.updated !== undefined) query.updated = params.updated;
  if (params.isPrimary !== undefined) query.isPrimary = params.isPrimary ? 1 : 0;
  if (params.country) query.country = params.country;
  if (params.eventName) query.eventName = params.eventName;

  return query;
};

export const generateStockSplitMessage = (
  symbol: string,
  companyName: string,
  ratio: string,
  date: string,
  t: TranslateFn = translate,
) => {
  let tense: string | null = null;

  if (!symbol && !ratio && !companyName && !date) return tense;

  const now = DateTime.local().setZone('America/New_York', { keepLocalTime: false });
  const datetime = DateTime.fromFormat(`${date}`, 'yyyy-MM-dd');
  const isToday = datetime.hasSame(now, 'day');
  const isFuture = datetime.startOf('day') > now.startOf('day');
  const isPast = datetime.startOf('day') < now.startOf('day');

  if (isFuture) {
    tense = `${t('Quote.Reports.StockSplits.is-expected-to-split', {
      companyName: companyName || symbol,
      ns: 'quote',
    })}${' '}
    ${DateTime.fromISO(date).toFormat('DDDD')} ${t('Quote.Reports.StockSplits.with-a-ratio-of', { ns: 'quote' })}${' '}
    ${ratio}`;
  }

  if (isToday || isPast) {
    tense = `${t('Quote.Reports.StockSplits.recently-split-on', {
      companyName: companyName || symbol,
      ns: 'quote',
    })}${' '}
    ${DateTime.fromISO(date).toFormat('DDDD')} ${t('Quote.Reports.StockSplits.with-a-ratio-of', { ns: 'quote' })}${' '}
    ${ratio}`;
  }

  return tense;
};

export const generateDividendMessage = (
  exDividendDate: string,
  t: TranslateFn = translate,
  language = i18n.language,
): string | null => {
  let tense: string | null = null;
  if (!exDividendDate) return tense;

  const now = DateTime.local().setZone('America/New_York', { keepLocalTime: false });
  const datetime = DateTime.fromFormat(`${exDividendDate}`, 'yyyy-MM-dd');
  const isoDate = DateTime.fromISO(exDividendDate ?? '').setZone('America/New_York', { keepLocalTime: true });

  const isFuture = datetime > now;
  const isPast = datetime < now;
  const timeFromNow = isoDate
    .plus({ day: isFuture ? 1 : 0 })
    .setLocale(language)
    .toRelative();
  const isToday = datetime.hasSame(now, 'day');
  const daysFromNow = datetime.startOf('day').diff(now.startOf('day'), 'days').days;

  if (isToday) {
    tense = t('Quote.Reports.the-dividend-ex-date-is', {
      date: t('Date.Days.today'),
      ns: 'quote',
    });
  } else if (!isToday && isFuture && daysFromNow < 2) {
    tense = t('Quote.Reports.the-dividend-ex-date-is', {
      date: t('Date.Days.tomorrow'),
      ns: 'quote',
    });
  } else if (isPast) {
    tense = t('Quote.Reports.the-dividend-ex-date-was', { date: timeFromNow, ns: 'quote' });
  } else {
    tense = t('Quote.Reports.the-dividend-ex-date-is', { date: timeFromNow, ns: 'quote' });
  }

  return tense;
};

export const generateEarningsMessage = (
  date: string,
  time: string,
  eps: string,
  revenue: string,
  period: string,
  periodYear: number | string,
  t: TranslateFn = translate,
  language = i18n.language,
): string | null => {
  let tense: string | null = null;

  const now = DateTime.local().setZone('America/New_York', { keepLocalTime: false });
  const datetime = DateTime.fromISO(date ? `${date}T${time ?? '00:00'}` : '', { locale: 'en-US' });
  const isoDate = DateTime.fromISO(date ?? '', { locale: language }).setZone('America/New_York', {
    keepLocalTime: true,
  });

  const formattedDate = isoDate.toFormat('ccc MMM d', { locale: language }).concat(`${getNumberSuffix(isoDate.day)}`);
  const duringPeriod = marketTime(time, {
    afterMarket: t('Quote.Reports.DuringPeriod.after-market', { ns: 'quote' }),
    invalid: '',
    preMarket: t('Quote.Reports.DuringPeriod.pre-market', { ns: 'quote' }),
    regular: t('Quote.Reports.DuringPeriod.regular', { ns: 'quote' }),
  });

  const isFuture = datetime > now;
  const isPast = datetime < now;
  const timeFromNow = isoDate
    .plus({ day: isFuture ? 1 : 0 })
    .setLocale(language)
    .toRelative();
  const isToday = datetime.hasSame(now, 'day');
  const released: boolean = !isEmpty(eps) && !isEmpty(revenue) && (isPast || isToday);
  const daysFromNow = datetime.startOf('day').diff(now.startOf('day'), 'days').days;

  const periodTitle = `${period} ${periodYear}`;

  if (isToday && released) {
    tense = t('Quote.Reports.Earnings.earnings-were-released-today', {
      duringPeriod,
      ns: 'quote',
      period: periodTitle,
    });
  } else if (isToday && !released) {
    tense = t('Quote.Reports.Earnings.earnings-announcement', {
      duringPeriod,
      ns: 'quote',
      period: periodTitle,
    });
  } else if (!isToday && isFuture && daysFromNow < 2) {
    tense = t('Quote.Reports.Earnings.earnings-tomorrow-on', {
      duringPeriod,
      formattedDate,
      ns: 'quote',
      period: periodTitle,
    });
  } else if (!isToday && isFuture) {
    tense = t('Quote.Reports.Earnings.earnings-from-now-on', {
      duringPeriod,
      formattedDate,
      ns: 'quote',
      period: periodTitle,
      timeFromNow,
    });
  } else if (isPast && released) {
    tense = t('Quote.Reports.Earnings.earnings-were-released-on', {
      duringPeriod,
      formattedDate,
      ns: 'quote',
      period: periodTitle,
    });
  }
  return tense;
};

export const generateConferenceCalls = (
  date: string | null,
  startTime: string | null,
  t: TranslateFn = translate,
  language = i18n.language,
): string | null => {
  let tense: string | null = null;
  if (!date && !startTime) return tense;

  const now = DateTime.local().setZone('America/New_York', { keepLocalTime: false });
  const startDatetime = DateTime.fromISO(date ? `${date}T${startTime ?? '00:00'}` : '', { locale: language });
  const isoDate = DateTime.fromISO(date ?? '', { locale: language }).setZone('America/New_York', {
    keepLocalTime: true,
  });
  const timeDiff = now.diff(startDatetime, 'hours').hours;

  const isFuture = startDatetime > now;
  const timeFromNow = isoDate
    .plus({ day: isFuture ? 1 : 0 })
    .setLocale(language)
    .toRelative();
  const isToday = startDatetime.hasSame(now, 'day');
  const isPast = startDatetime < now;
  const daysFromNow = startDatetime.startOf('day').diff(now.startOf('day'), 'days').days;

  const callTime = startDatetime.toFormat('h:mm a');

  // Conference calls rarely last over 60 minutes
  const MAX_CALL_TIME_ESTIMATE = 1;

  if (isToday) {
    if (timeDiff < 0) {
      tense = t('Quote.Reports.ConferenceCall.scheduled-at-today', { callTime, ns: 'quote' });
    } else if (timeDiff < MAX_CALL_TIME_ESTIMATE) {
      tense = t('Quote.Reports.ConferenceCall.in-progress', { ns: 'quote' });
    } else {
      const hoursAgo = startDatetime.setLocale(language).toRelative();
      tense = t('Quote.Reports.ConferenceCall.started-over', { hoursAgo, ns: 'quote' });
    }
  } else if (!isToday && isFuture && daysFromNow < 2) {
    tense = t('Quote.Reports.ConferenceCall.scheduled-at-tomorrow', { callTime, ns: 'quote' });
  } else if (isPast) {
    tense = t('Quote.Reports.ConferenceCall.started-recent', { callTime, ns: 'quote', timeFromNow });
  } else {
    tense = t('Quote.Reports.ConferenceCall.scheduled-at', { callTime, ns: 'quote', timeFromNow });
  }
  return tense;
};

export const generateMAText = (maSummary: MergersAndAcquisitions): string => {
  const excludedTargetNames = ['Terms Not Disclosed'];

  let result = '';
  if (!maSummary) return result;
  if (maSummary.deal_status === 'Cancelled') return result;
  if (!['Acquisition', 'Merger', 'Asset Sale'].includes(maSummary.deal_type)) return result;

  const now = DateTime.local().setZone('America/New_York', { keepLocalTime: false });
  const datetime = DateTime.fromFormat(`${maSummary.date}`, 'yyyy-MM-dd');
  const isToday = datetime.hasSame(now, 'day');

  if (maSummary.target_name) {
    if (excludedTargetNames.includes(maSummary.target_name)) {
      result = `- `;
    } else {
      result = `${maSummary.target_name} ${maSummary.target_ticker ? `(${maSummary.target_ticker}) ` : ''}`;
    }
  }

  const isAnnouncedToday = maSummary?.deal_status === 'Announced' && isToday;
  const actionString = generateMAActionText(
    maSummary?.deal_status,
    maSummary?.deal_type,
    maSummary?.date,
    isAnnouncedToday,
  );

  if (!actionString) return '';

  result = result + actionString;
  result = result + `${maSummary.acquirer_name} ${maSummary.acquirer_ticker ? `(${maSummary.acquirer_ticker})` : ''}`;
  if (!isAnnouncedToday) {
    result = result + generateMADateText(maSummary?.deal_status, maSummary?.date_completed, maSummary?.date, isToday);
  }
  result = result + generateMADealText(maSummary?.deal_size, maSummary?.deal_payment_type, maSummary?.currency);

  return result;
};

export interface CustomTimeLabels {
  afterMarket?: string;
  invalid?: string | null;
  preMarket?: string;
  regular?: string;
}

export enum MarketSession {
  afterHours = 'After Hours',
  preMarket = 'PreMarket',
  regular = 'Regular',
}

const marketTime = (time: string, custom: CustomTimeLabels = {}) => {
  const preMarket = DateTime.fromFormat('09:30:00', 'HH:mm:ss');
  const afterMarket = DateTime.fromFormat('16:00:00', 'HH:mm:ss');
  const thisMoment = DateTime.fromFormat(time, 'HH:mm:ss');

  if (thisMoment < preMarket) {
    return custom.preMarket || MarketSession.preMarket;
  } else if (thisMoment >= afterMarket) {
    return custom.afterMarket || MarketSession.afterHours;
  } else if (thisMoment >= preMarket && thisMoment < afterMarket) {
    return custom.regular || MarketSession.regular;
  } else if (Object.prototype.hasOwnProperty.call(custom, 'invalid')) {
    return custom.invalid;
  }

  return time;
};

export const generateMADateText = (dealStatus: string, dateCompleted: string, date: string, isToday: boolean) => {
  if (isToday) {
    return ` today `;
  } else {
    if (dealStatus === 'Completed') {
      return ` on ${DateTime.fromISO(dateCompleted).toFormat('DDDD')}`;
    } else {
      return ` on ${DateTime.fromISO(date).toFormat('DDDD')}`;
    }
  }
};

export const generateMADealText = (dealSize: string, dealPaymentType: string, currency: string) => {
  const formattedDealSize = currency
    ? abbreviateNumber(Number(dealSize), { currency })
    : abbreviateNumber(Number(dealSize));
  if (dealSize) {
    return `${formattedDealSize ? ` for ${formattedDealSize} ` : ''} ${
      dealPaymentType ? `paid in ${dealPaymentType.toLowerCase()}` : ''
    }`;
  } else {
    return '';
  }
};

export const generateMAActionText = (maStatus: string, dealType: string, date: string, isAnnouncedToday: boolean) => {
  const now = DateTime.local().setZone('America/New_York', { keepLocalTime: false });
  const datetime = DateTime.fromFormat(`${date}`, 'yyyy-MM-dd');
  const isPast = datetime.startOf('day') < now.startOf('day');

  const formatAnnouncedTodayText = (text: string, isAnnouncedToday: boolean) => {
    return isAnnouncedToday ? text.replace('announced', 'announced today') : text;
  };

  if (dealType === 'Acquisition') {
    if (maStatus === 'Rumored' && isPast) {
      return 'was rumored to be acquired by ';
    } else if (maStatus === 'Rumored') {
      return 'is rumored to be acquired by ';
    } else if (maStatus === 'Completed') {
      return 'was acquired by ';
    } else if (maStatus === 'Announced') {
      return formatAnnouncedTodayText('announced it will be acquired by ', isAnnouncedToday);
    } else if (maStatus === 'Canceled') {
      return 'acquisition was canceled with ';
    } else if (maStatus === 'Regulatory Review') {
      return 'acquisition is under regulatory review with ';
    } else {
      return '';
    }
  } else if (dealType === 'Merger') {
    if (maStatus === 'Rumored' && isPast) {
      return 'was rumored to be merged with ';
    } else if (maStatus === 'Rumored') {
      return 'is rumored to be merged with ';
    } else if (maStatus === 'Completed') {
      return 'was merged with ';
    } else if (maStatus === 'Announced') {
      return formatAnnouncedTodayText('announced it will be merged with ', isAnnouncedToday);
    } else if (maStatus === 'Canceled') {
      return 'merger was canceled with ';
    } else if (maStatus === 'Regulatory Review') {
      return 'merger is under regulatory review with ';
    } else {
      return '';
    }
  } else if (dealType === 'Asset Sale') {
    if (maStatus === 'Rumored' && isPast) {
      return 'was rumored to sell assets to ';
    } else if (maStatus === 'Rumored') {
      return 'is rumored to sell assets to ';
    } else if (maStatus === 'Completed') {
      return 'sold assets to ';
    } else if (maStatus === 'Announced') {
      return formatAnnouncedTodayText('announced it will sell assets to ', isAnnouncedToday);
    } else if (maStatus === 'Canceled') {
      return 'asset sale was canceled with ';
    } else if (maStatus === 'Regulatory Review') {
      return 'asset sale is under regulatory review with ';
    } else {
      return '';
    }
  } else {
    return '';
  }
};
